import { useMemo } from "react";

import {
    combineCollections,
    filterNullableRecord,
    filterTruthy,
    getUnixTs,
    lamportsToUiAmount,
    NullableRecord,
    SortDirection,
    TIME,
    ALLOWED_FEATURES
} from "@bridgesplit/utils";
import { useMemoizedKeyMap } from "@bridgesplit/ui";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import Decimal from "decimal.js";

import {
    ExternalPointReward,
    LendingVaultAllocationExpanded,
    LendingVaultExpanded,
    LendVaultInfoFilter,
    LendVaultSortType,
    LendVaultsPagination,
    LoopscalePointsAction,
    ParsedAllowedStakeDuration,
    ParsedUserVaultStake,
    ParsedVaultRewardsScheduleExpanded,
    StakeStatus,
    StrategyInfoExpanded,
    UserExternalRewardsFilter,
    UserExternalRewardsPnlInfoExpanded,
    UserLendingVaultFilter,
    UserVaultExternalRewardsPnlResponse,
    UserVaultPnl,
    UserVaultPositionExpanded,
    DepositTermSummary,
    VaultReward
} from "../types/";
import {
    useTokenListMetadataByMints,
    useLendingVaultAllocationsQuery,
    useLendingVaultInfosQuery,
    useLendingVaultPositionsQuery,
    useUserPositionDepositTermsQuery,
    useUserGenesisAccessQuery,
    useUserVaultExternalRewardsPnlQuery
} from "../reducers";
import { useOraclePrices } from "./pricing";
import { useAbfTypesToUiConverter } from "../utils/ui";
import { useMarketOracleInfos, useStrategyDurations } from "./markets";
import { useCurrentTime } from "./points";
import { useActiveWallet } from "./wallet";

type Params = {
    skip?: boolean;
    pagination: LendVaultsPagination;
    filter: LendVaultInfoFilter;
};

export function useLendVaults({ pagination, filter, skip: skipParam }: Params) {
    const query = useLendingVaultInfosQuery(
        { ...pagination, ...filter },
        {
            skip: skipParam
        }
    );

    const vaultPrincipalMints = query.data?.lendVaults.map((v) => v.vault.principalMint);
    const collateralMints = query.data?.lendVaults.flatMap((v) => Object.keys(v.vaultStrategy.terms));
    const rewardMints = query.data?.lendVaults.flatMap((v) => v.vaultRewardsSchedules?.map((r) => r.rewardMint) ?? []);

    const mints = combineCollections([vaultPrincipalMints, collateralMints, rewardMints]);

    const { strategyDurations } = useStrategyDurations();

    const { getMetadata, isLoading: metadataLoading } = useTokenListMetadataByMints(
        combineCollections([vaultPrincipalMints, collateralMints])
    );

    const { data: marketData } = useMarketOracleInfos({
        filter: {
            principalMints: vaultPrincipalMints ?? [],
            addresses: query.data?.lendVaults?.map((v) => v.vaultStrategy.strategy.marketInformation) ?? [],
            verified: false
        }
    });

    const { getOracle, isLoading: oracleLoading } = useOraclePrices(mints);
    const isFetching = query.isFetching;
    const { convertLendingVaultInfo, convertLendingVaultLtvInfo, tokensLoading } =
        useAbfTypesToUiConverter(vaultPrincipalMints);
    const isLoading = metadataLoading || oracleLoading || query.isLoading || tokensLoading;

    const data = useMemo(() => {
        if (!query.data || isLoading) return undefined;

        return query.data.lendVaults
            .map((vaultInfoRaw): NullableRecord<LendingVaultExpanded> => {
                const vaultInfo = convertLendingVaultInfo(vaultInfoRaw);

                // Find matching market information for this vault
                const vaultMarketInfo = marketData?.find(
                    (info) => info.marketInformation.address === vaultInfo.vaultStrategy.strategy.marketInformation
                );

                // Updated terms processing for new structure
                const ltvInfos = convertLendingVaultLtvInfo(
                    vaultInfoRaw,
                    vaultMarketInfo,
                    strategyDurations,
                    getMetadata
                );

                return {
                    ...vaultInfo,
                    principalMetadata: getMetadata(vaultInfo.vault.principalMint),
                    principalOracle: getOracle(vaultInfo.vault.principalMint),
                    ltvInfos
                };
            })
            .filter(filterNullableRecord);
    }, [
        query.data,
        isLoading,
        convertLendingVaultInfo,
        marketData,
        convertLendingVaultLtvInfo,
        strategyDurations,
        getMetadata,
        getOracle
    ]);

    return {
        data,
        totalDataCount: query.data?.total,
        isLoading,
        isFetching
    };
}

export function useLendVaultByAddress(vaultAddress: string | undefined) {
    const { data: allVaults } = useLendVaults({
        pagination: {
            page: 0,
            pageSize: 1,
            sortType: LendVaultSortType.TotalDeposits,
            sortDirection: SortDirection.Asc
        },
        filter: { vaultAddresses: vaultAddress ? [vaultAddress] : undefined },
        skip: !vaultAddress
    });
    return { data: allVaults?.find((v) => v.vault.address === vaultAddress) };
}

export function getVaultLpVirtualPrice(vaultExpanded: LendingVaultExpanded | undefined, currentTimestamp: number) {
    if (!vaultExpanded) return undefined;
    const netAssetValue = getVaultNetAssetValue(vaultExpanded, currentTimestamp);
    const lpSupply = vaultExpanded.vault.lpSupply;
    return netAssetValue ? netAssetValue / lpSupply : undefined;
}

export function getVaultNetAssetValue(
    vaultExpanded: LendingVaultExpanded | undefined,
    currentTimestamp: number,
    options: { ignoreFees: boolean } = { ignoreFees: false }
) {
    if (!vaultExpanded) return undefined;
    const { ignoreFees = false } = options;
    const principalDeployed = vaultExpanded.vaultStrategy.strategy.currentDeployedAmount;
    const interestPerSecond = vaultExpanded.vaultStrategy.strategy.interestPerSecond;
    const principalTokenBalance = vaultExpanded.vaultStrategy.strategy.tokenBalance;
    const outstandingInterestAmount = vaultExpanded.vaultStrategy.strategy.outstandingInterestAmount;
    const timeElapsed = Math.max(0, currentTimestamp - vaultExpanded.vaultStrategy.strategy.lastAccruedTimestamp);
    const interestEarned = interestPerSecond * timeElapsed + outstandingInterestAmount;
    const externalYieldBalance = vaultExpanded.vaultStrategy.externalYieldInfo?.balance ?? 0;
    const tvl = principalDeployed + interestEarned + externalYieldBalance + principalTokenBalance;
    if (ignoreFees) return tvl;
    const fees = getCurrentVaultFees(vaultExpanded, currentTimestamp);
    return Math.max(0, tvl - fees);
}

export function getVaultTvl(vaultExpanded: LendingVaultExpanded | undefined, currentTimestamp: number) {
    return getVaultNetAssetValue(vaultExpanded, currentTimestamp, { ignoreFees: true });
}

function convertToUserVaultPosition({
    userPosition,
    vaultExpanded,
    currentTimestamp,
    index,
    active
}: {
    userPosition: ParsedUserVaultStake;
    vaultExpanded: LendingVaultExpanded;
    currentTimestamp: number;
    index: number;
    active: boolean;
}): UserVaultPositionExpanded {
    const amountAvailable =
        userPosition.amountStaked -
        (calculateEarlyUnstakeFee({ userPosition, vaultExpanded, currentTimestamp })?.feeLp ?? 0);
    const totalAmount = userPosition.amountStaked;

    const lpPrice = getVaultLpVirtualPrice(vaultExpanded, currentTimestamp);

    const totalValuePrincipal = totalAmount * (lpPrice ?? 0);
    const totalValueUsd = totalValuePrincipal * vaultExpanded.principalOracle.usdPrice;

    const amountAvailablePrincipal = amountAvailable * (lpPrice ?? 0);
    const amountAvailableUsd = amountAvailablePrincipal * vaultExpanded.principalOracle.usdPrice;

    return {
        index,
        address: userPosition.address,
        vaultExpanded,
        amountAvailable: {
            amountLp: amountAvailable,
            amountUsd: amountAvailableUsd,
            amountPrincipal: amountAvailablePrincipal
        },
        totalAmount: {
            amountLp: totalAmount,
            amountUsd: totalValueUsd,
            amountPrincipal: totalValuePrincipal
        },
        active
    };
}

export function calculateEarlyUnstakeFee({
    userPosition,
    vaultExpanded,
    currentTimestamp
}: {
    userPosition: ParsedUserVaultStake;
    vaultExpanded: LendingVaultExpanded;
    currentTimestamp: number;
}) {
    if ((userPosition.unstakeTime ?? 0) !== 0) {
        return { feeLp: userPosition.unstakeFeeApplied };
    }

    const maxEarlyUnstakeFee = vaultExpanded.vault.maxEarlyUnstakeFee;
    const timeLeftInStake = Math.max(0, userPosition.endTime - currentTimestamp);
    const totalStakeTime = userPosition.endTime - userPosition.startTime;
    if (totalStakeTime === 0) return { feeLp: 0 };
    const fee = maxEarlyUnstakeFee * (timeLeftInStake / totalStakeTime);
    const feeLp = fee * userPosition.amountStaked;

    return { feeLp };
}

export function useUserVaultPositionByAddress(vaultExpanded: LendingVaultExpanded | undefined) {
    const { data, isLoading, isFetching } = useUserLendingVaultPositionInfo({
        pagination: {
            page: 0,
            pageSize: 1,
            sortType: LendVaultSortType.TotalDeposits,
            sortDirection: SortDirection.Asc,
            status: StakeStatus.Active
        },
        filter: {
            status: StakeStatus.Active,
            vaultAddresses: vaultExpanded ? [vaultExpanded.vault.address] : undefined
        },
        skip: !vaultExpanded
    });

    if (!data?.length || !vaultExpanded) {
        return { data: undefined, isLoading, isFetching };
    }

    const userPosition = data.find((p) => p.vaultExpanded.vault.address === vaultExpanded.vault.address);

    return { data: userPosition, isLoading, isFetching };
}

// Fetches all user vault positions
export function useUserVaultPositionByVault(vaultExpanded: LendingVaultExpanded | undefined) {
    const { activeWallet } = useActiveWallet();

    const filter: UserLendingVaultFilter = {
        vaultAddresses: vaultExpanded ? [vaultExpanded.vault.address] : undefined,
        sortType: LendVaultSortType.WAvgApy,
        sortDirection: SortDirection.Asc,
        page: 0,
        pageSize: 1,
        status: StakeStatus.Active
    };

    const query = useUserLendingVaultPositionInfo({
        pagination: filter,
        skip: !vaultExpanded || !activeWallet
    });

    return query;
}

export function useUserVaultPositionPnl(vaultExpanded?: LendingVaultExpanded | undefined) {
    const { activeWallet } = useActiveWallet();

    const {
        data: externalRewardsPnlResponse,
        isLoading: externalRewardsPnlLoading,
        isFetching: externalRewardsPnlFetching
    } = useUserVaultExternalRewardsPnlQuery(
        {
            vaultAddresses: vaultExpanded ? [vaultExpanded.vault.address] : [],
            stakeAccounts: [],
            rewardMints: []
        },
        {
            pollingInterval: 10_000,
            skip: !activeWallet
        }
    );

    const rewardMints = extractRewardMints(externalRewardsPnlResponse, vaultExpanded);
    const { convertExternalRewardsPnlResponse } = useAbfTypesToUiConverter(rewardMints);

    const data: UserExternalRewardsPnlInfoExpanded[] | undefined = useMemo(() => {
        if (!externalRewardsPnlResponse) return undefined;

        const result = convertExternalRewardsPnlResponse(externalRewardsPnlResponse);

        return result;
    }, [externalRewardsPnlResponse, convertExternalRewardsPnlResponse]);

    return {
        data,
        isLoading: externalRewardsPnlLoading,
        isFetching: externalRewardsPnlFetching
    };
}

export function useUserLendingVaultPositions(pagination: UserLendingVaultFilter, skip?: boolean) {
    const { activeWallet } = useActiveWallet();

    const query = useUserLendingVaultPositionInfo({
        pagination: pagination,
        skip: skip || !activeWallet
    });

    return query;
}

type UserPositionParamsFilter = Omit<UserLendingVaultFilter, "page" | "pageSize" | "sortType" | "sortDirection"> &
    Partial<UserExternalRewardsFilter>;
type UserPositionParams = {
    skip?: boolean;
    pagination: UserLendingVaultFilter;
    filter?: UserPositionParamsFilter;
};

export function useUserVaultDepositTermsByVault(vaultAddress: string | undefined) {
    const { data, isLoading, isFetching } = useUserPositionDepositTermsQuery(
        { vaultAddresses: vaultAddress ? [vaultAddress] : [] },
        { skip: !vaultAddress }
    );

    return { data: vaultAddress ? data?.[vaultAddress] : undefined, isLoading, isFetching };
}

export function useUserVaultDepositTerms() {
    const { data, isLoading, isFetching } = useUserPositionDepositTermsQuery({ vaultAddresses: [] });

    return { data, isLoading, isFetching };
}

export function useUserLendingVaultPositionInfo({ pagination, skip, filter }: UserPositionParams) {
    const { convertUserVaultStake } = useAbfTypesToUiConverter(filter?.principalMints ?? []);

    const query = useLendingVaultPositionsQuery(
        {
            ...pagination,
            ...filter
        },
        {
            skip: skip
        }
    );

    const vaultAddresses = query.data?.positions?.map((p) => p.vaultAddress);

    const {
        data: vaults,
        isLoading: vaultsLoading,
        isFetching: vaultsFetching
    } = useLendVaults({
        skip: skip,
        pagination: pagination,
        filter: { vaultAddresses }
    });

    const addressToVault = useMemoizedKeyMap(vaults, (v) => v.vault.address);

    const now = getUnixTs();

    const data: UserVaultPositionExpanded[] | undefined = useMemo(() => {
        if (vaultsLoading) return undefined;
        if (!query.data?.positions || !vaults) return [];

        const positions = query.data.positions
            .map((position, index) => {
                if (!addressToVault?.get(position.vaultAddress)) return null;

                const vaultExpanded = addressToVault.get(position.vaultAddress);
                if (!vaultExpanded) return null;

                const convertedPosition = convertToUserVaultPosition({
                    userPosition: convertUserVaultStake(position, vaultExpanded.vault.principalMint),
                    vaultExpanded,
                    currentTimestamp: now,
                    index,
                    active: pagination.status === StakeStatus.Active
                });

                return convertedPosition;
            })
            .filter(filterTruthy);
        return positions;
    }, [vaultsLoading, query.data?.positions, vaults, addressToVault, convertUserVaultStake, now, pagination.status]);

    const isLoading = query.isLoading || vaultsLoading;
    const isFetching = query.isFetching || vaultsFetching;

    return {
        data,
        isLoading,
        isFetching,
        totalDataCount: query.data?.total
    };
}

export function useLendingVaultAllocation(vaultAddress: string | undefined, principalMint: string | undefined) {
    const query = useLendingVaultAllocationsQuery(vaultAddress ? { vaultAddress } : skipToken, {
        skip: !vaultAddress || !principalMint
    });

    const collateralMints = query.data?.map((d) => d.collateralMint);
    const mints = combineCollections([collateralMints, [principalMint]]);
    const { getMetadata, isLoading: metadataLoading } = useTokenListMetadataByMints(mints);
    const { convertLendingVaultAllocation } = useAbfTypesToUiConverter(mints);

    const isLoading = query.isLoading || metadataLoading;
    const data = useMemo(() => {
        if (isLoading) return undefined;
        return query.data
            ?.map((rawAllocation): NullableRecord<LendingVaultAllocationExpanded> => {
                const collateralMetadata = getMetadata(rawAllocation.collateralMint);
                const principalMetadata = getMetadata(principalMint);
                const allocation = convertLendingVaultAllocation(rawAllocation, principalMetadata?.decimals ?? 0);
                return {
                    ...allocation,
                    collateralMetadata
                };
            })
            .filter(filterNullableRecord);
    }, [isLoading, query.data, getMetadata, principalMint, convertLendingVaultAllocation]);
    return { data, isLoading };
}

export function getVaultIdleBalance(
    vaultExpanded: LendingVaultExpanded,
    currentTimestamp: number,
    options: { ignoreFees: boolean } = { ignoreFees: false }
) {
    let totalIdleBalance = 0;
    if (vaultExpanded.vaultStrategy.externalYieldInfo) {
        totalIdleBalance += vaultExpanded.vaultStrategy.externalYieldInfo.balance;
    }
    totalIdleBalance += vaultExpanded.vaultStrategy.strategy.tokenBalance;
    if (options.ignoreFees) return totalIdleBalance;
    return Math.max(0, totalIdleBalance - getCurrentVaultFees(vaultExpanded, currentTimestamp));
}

export function getVaultBalanceAvailableToWithdraw(vaultExpanded: LendingVaultExpanded, currentTimestamp: number) {
    return getVaultIdleBalance(vaultExpanded, currentTimestamp, { ignoreFees: true });
}

export function getVaultInterestAccrued(vaultExpanded: LendingVaultExpanded, currentTimestamp: number) {
    return (
        vaultExpanded.vaultStrategy.strategy.interestPerSecond *
        (currentTimestamp - vaultExpanded.vaultStrategy.strategy.lastAccruedTimestamp)
    );
}

export function getCurrentVaultFees(vaultExpanded: LendingVaultExpanded, currentTimestamp: number) {
    const interestAccruedValue = getVaultInterestAccrued(vaultExpanded, currentTimestamp);
    return (
        interestAccruedValue * vaultExpanded.vaultStrategy.strategy.interestFee +
        vaultExpanded.vaultStrategy.strategy.feeClaimable
    );
}

type VaultAddress = string;
const VAULT_EXTERNAL_POINT_REWARDS: Record<VaultAddress, ExternalPointReward[]> = {
    "7n5F6vLutwTPuVju9t4ZC22vHyJNyGbHKzaokdyWycjy": [ExternalPointReward.Fragmetric]
};
export function getVaultExternalPointRewards(
    vaultExpanded: LendingVaultExpanded | undefined
): ExternalPointReward[] | undefined {
    if (!vaultExpanded) return undefined;

    const vaultAddress = vaultExpanded.vault.address;
    if (!VAULT_EXTERNAL_POINT_REWARDS[vaultAddress]) return undefined;
    return VAULT_EXTERNAL_POINT_REWARDS[vaultAddress];
}

export function getVaultLoopscalePoints(vaultExpanded: LendingVaultExpanded | undefined) {
    // Default to liquid stake for now
    if (!vaultExpanded) return LoopscalePointsAction.LiquidStake;
    if (vaultExpanded.vaultMetadata?.isGenesisVault) return LoopscalePointsAction.GenesisLiquidStake;
    return LoopscalePointsAction.LiquidStake;
}

export function getVaultLoopscalePointsFromStakeRewards(stakeRewards: VaultReward | undefined) {
    if (!stakeRewards) return LoopscalePointsAction.LiquidStake;
    if (stakeRewards.baseReward.reward) {
        return stakeRewards.baseReward.reward as LoopscalePointsAction;
    }
    return LoopscalePointsAction.LiquidStake;
}

export function isRecentVaultDepositOrWithdrawal(mostRecentDepositOrWithdrawalTimestamp: number | undefined): boolean {
    if (!mostRecentDepositOrWithdrawalTimestamp) return false;

    const isRecent = Math.max(getUnixTs() - mostRecentDepositOrWithdrawalTimestamp, 0) < MIN_TIME_FOR_VAULT_PNL_PENDING;

    return isRecent;
}

export function isExternalYieldUpdated(
    vaultExpanded: LendingVaultExpanded | undefined,
    userVaultPnl: UserVaultPnl | undefined
) {
    if (!vaultExpanded || !userVaultPnl?.mostRecentDepositOrWithdrawalTimestamp) return false;
    if (!vaultExpanded.vaultStrategy.externalYieldInfo) return true;

    return (
        vaultExpanded.vaultStrategy.externalYieldInfo?.lastUpdateTime >=
        userVaultPnl.mostRecentDepositOrWithdrawalTimestamp
    );
}

export const MIN_TIME_FOR_VAULT_PNL_PENDING = TIME.MINUTE * 15;

export function useUserVaultPnlFromDepositTerms(
    depositTerms: DepositTermSummary | undefined,
    vaultExpanded: LendingVaultExpanded | undefined,
    userPosition: UserVaultPositionExpanded | undefined
): UserVaultPnl | undefined {
    const { now } = useCurrentTime(10_000);

    const currentPnl = calculateVaultCurrentPnlFromDepositTerms(depositTerms, userPosition, vaultExpanded, now);
    const historicalPnl = calculateVaultHistoricalPnlFromDepositTerms(vaultExpanded, depositTerms);
    const lifetimePnl = historicalPnl !== undefined ? historicalPnl + (currentPnl ?? 0) : undefined;

    const userVaultPnl: UserVaultPnl = {
        currentPnl,
        lifetimePnl,
        mostRecentDepositOrWithdrawalTimestamp: depositTerms?.mostRecentDepositOrWithdrawalTimestamp
    };

    return userVaultPnl;
}

function calculateVaultCurrentPnlFromDepositTerms(
    depositTerms: DepositTermSummary | undefined,
    userPosition: UserVaultPositionExpanded | undefined,
    vaultExpanded: LendingVaultExpanded | undefined,
    currentTimestamp: number
) {
    if (!depositTerms?.currentFlows || !vaultExpanded || !userPosition) return undefined;
    const virtualPrice = getVaultLpVirtualPrice(vaultExpanded, currentTimestamp);
    if (!virtualPrice) return undefined;

    const positionValue = virtualPrice * userPosition.totalAmount.amountLp;
    const netFlows = lamportsToUiAmount(
        depositTerms.currentFlows.withdrawals - depositTerms.currentFlows.deposits,
        vaultExpanded.principalMetadata.decimals
    );
    return positionValue + netFlows;
}

function calculateVaultHistoricalPnlFromDepositTerms(
    vaultExpanded: LendingVaultExpanded | undefined,
    depositTerms: DepositTermSummary | undefined
) {
    if (!depositTerms?.lifetimeCapitalFlows || !vaultExpanded) return undefined;

    return lamportsToUiAmount(
        depositTerms.lifetimeCapitalFlows.withdrawals - depositTerms.lifetimeCapitalFlows.deposits,
        vaultExpanded.principalMetadata.decimals
    );
}

export function isRewardsActive(schedule: ParsedVaultRewardsScheduleExpanded) {
    return schedule.rewardEndTime > getUnixTs() && schedule.rewardStartTime < getUnixTs();
}

//Util function to aggregate external rewards pnl
function extractRewardMints(
    externalRewardsPnlResponse: UserVaultExternalRewardsPnlResponse | undefined,
    vaultExpanded: LendingVaultExpanded | undefined
): string[] {
    const rewardMints: string[] = [];

    if (externalRewardsPnlResponse && vaultExpanded) {
        const vaultRewards = externalRewardsPnlResponse[vaultExpanded.vault.address];
        if (vaultRewards) {
            Object.values(vaultRewards).forEach((rewardsForStake) => {
                Object.keys(rewardsForStake).forEach((mint) => {
                    if (!rewardMints.includes(mint)) {
                        rewardMints.push(mint);
                    }
                });
            });
        }
    }

    return rewardMints;
}

export function summarizeRewardsSchedule(
    schedule: ParsedVaultRewardsScheduleExpanded,
    vaultEmittedRewards: Record<number, number> | undefined
) {
    const timeSinceRewardsStart = getUnixTs() - schedule.rewardStartTime;

    const totalEmittedSinceStartTime = lamportsToUiAmount(
        vaultEmittedRewards?.[schedule.scheduleIndex] ?? 0,
        schedule.rewardMetadata.decimals
    );

    return {
        isActive: isRewardsActive(schedule),
        totalEmittedSinceStartTime,
        timeSinceRewardsStart
    };
}

export function getVaultStakeWeightDurations() {
    return [
        { duration: ParsedAllowedStakeDuration.ZeroDays, label: "Liquid stake" },
        { duration: ParsedAllowedStakeDuration.OneMonth, label: "1 Month" },
        { duration: ParsedAllowedStakeDuration.ThreeMonths, label: "3 Months" },
        { duration: ParsedAllowedStakeDuration.SixMonths, label: "6 Months" },
        { duration: ParsedAllowedStakeDuration.OneYear, label: "1 Year" }
    ];
}

export function recalculateStrategyApyWithDeposit({
    lendingStrategyExpanded,
    depositAmount
}: {
    lendingStrategyExpanded: StrategyInfoExpanded;
    depositAmount: number;
}) {
    const idleApy = new Decimal(lendingStrategyExpanded?.externalYieldInfo?.apy ?? 0);

    const idleYieldBalance = new Decimal(lendingStrategyExpanded?.externalYieldInfo?.balance ?? 0);

    const idleYieldBalanceWithDeposit = idleYieldBalance.add(new Decimal(depositAmount));

    const currentDeployedAmount = new Decimal(lendingStrategyExpanded?.strategy.currentDeployedAmount ?? 0);

    const tokenBalance = new Decimal(lendingStrategyExpanded?.strategy.tokenBalance ?? 0);

    const totalDeposits = currentDeployedAmount.add(new Decimal(depositAmount)).add(idleYieldBalance).add(tokenBalance);

    if (totalDeposits.eq(0)) return 0;

    const newApy = idleApy
        .mul(idleYieldBalanceWithDeposit)
        .add(currentDeployedAmount.mul(lendingStrategyExpanded?.strategy.fixedApy))
        .div(totalDeposits);

    return newApy.toNumber();
}

export function getLendingVaultMints(lendingVaultExpanded: LendingVaultExpanded | undefined) {
    const rewardMints = lendingVaultExpanded?.vaultRewardsSchedules.map((schedule) => schedule.rewardMetadata.mint);
    const principalMint = lendingVaultExpanded?.vault.principalMint;
    const rewardMintsWithPrincipal = combineCollections([rewardMints, [principalMint]]);
    return rewardMintsWithPrincipal;
}

export function useVaultApy({
    lendingVaultExpanded,
    includeRewards = true,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    includeRewards?: boolean;
    depositAmount?: number;
}): number | undefined {
    const rewardTokenMintsWithPrincipal = getLendingVaultMints(lendingVaultExpanded);
    const { getUsdPrice } = useOraclePrices(rewardTokenMintsWithPrincipal);

    if (!lendingVaultExpanded || !rewardTokenMintsWithPrincipal) return undefined;

    const baseApy =
        depositAmount > 0
            ? recalculateStrategyApyWithDeposit({
                  lendingStrategyExpanded: lendingVaultExpanded.vaultStrategy,
                  depositAmount
              })
            : lendingVaultExpanded.vaultStrategy.strategy.wAvgApy;

    if (!includeRewards) return baseApy;

    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;
    const rewardApys = lendingVaultExpanded.vaultRewardsSchedules
        .filter((schedule) => isRewardsActive(schedule))
        .map((schedule) => {
            const rewardPrice = getUsdPrice(schedule.rewardMetadata.mint);
            return rewardPrice !== undefined
                ? convertExternalRewardToApy(schedule, lpPrice, principalPrice, lpSupply, rewardPrice, depositAmount)
                : undefined;
        })
        .filter(filterTruthy)
        .reduce((total, apy) => total + apy, 0);

    return baseApy + rewardApys;
}

export function convertExternalRewardToApy(
    schedule: ParsedVaultRewardsScheduleExpanded,
    lpPrice: number | undefined,
    principalPrice: number | undefined,
    lpSupply: number,
    rewardTokenPrice: number,
    depositAmount = 0
) {
    const { emissionsPerSecond } = schedule;

    const emissionsPerYear = new Decimal(emissionsPerSecond).mul(new Decimal(TIME.YEAR));
    const lpSupplyDecimal = new Decimal(lpSupply);
    if (!lpPrice || !principalPrice) return undefined;

    // new lp amount if deposited = depositAmount * lpPrice + lp supply

    const newLpSupply =
        depositAmount > 0 ? lpSupplyDecimal.add(new Decimal(depositAmount).mul(lpPrice)) : lpSupplyDecimal;

    const denominator = newLpSupply.mul(lpPrice).mul(principalPrice);
    const numerator = emissionsPerYear.mul(rewardTokenPrice);
    const annualYield = numerator.div(denominator).toNumber();

    return annualYield;
}

export function useUserGenesisWhitelisted() {
    const { activeWallet } = useActiveWallet();
    const isGenesisWhitelistFlagOn = ALLOWED_FEATURES.genesisWhitelistOnly;

    const { data: hasAccess, isLoading } = useUserGenesisAccessQuery(undefined, {
        skip: !activeWallet || !isGenesisWhitelistFlagOn
    });

    const isUserGenesisWhitelisted = isGenesisWhitelistFlagOn ? hasAccess : true;

    return { isUserGenesisWhitelisted, isLoading };
}

export function isVaultDepositButtonEnabledWithGenesisRelease(
    vaultExpanded: LendingVaultExpanded | undefined,
    isUserGenesisWhitelisted: boolean | undefined
) {
    const isGenesisWhitelistFlagOn = ALLOWED_FEATURES.genesisWhitelistOnly;
    const { supplyLimitReached } = summarizeVaultSuppliedDeposits(vaultExpanded);
    if (supplyLimitReached) return false;

    if (!isGenesisWhitelistFlagOn) return true;
    if (!vaultExpanded?.vaultMetadata?.isGenesisVault) return true;
    if (!isUserGenesisWhitelisted) return false;

    return true;
}

export function summarizeVaultSuppliedDeposits(vaultExpanded: LendingVaultExpanded | undefined) {
    const principalLimit = vaultExpanded?.vaultMetadata?.depositCap ?? 0;
    const principalSupplied = getVaultTvl(vaultExpanded, getUnixTs()) ?? 0;
    const supplyLimitReached = principalLimit > 0 ? principalSupplied >= principalLimit : false;

    return {
        isDepositRestricted: !!vaultExpanded?.vaultMetadata?.depositCap,
        principalSupplied,
        supplyLimitReached,
        principalLimit
    };
}

export function isVaultNavigateDisabled(
    vaultExpanded: LendingVaultExpanded | undefined,
    isUserGenesisWhitelisted: boolean | undefined
) {
    const isGenesisWhitelistFlagOn = ALLOWED_FEATURES.genesisWhitelistOnly;
    return isGenesisWhitelistFlagOn && vaultExpanded?.vaultMetadata?.isGenesisVault && !isUserGenesisWhitelisted;
}

export function convertPrincipalToLpTokenAmount(
    principalAmount: number | undefined,
    vaultExpanded: LendingVaultExpanded | undefined
) {
    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(vaultExpanded, now);
    if (!lpPrice || !principalAmount) return undefined;

    return principalAmount / lpPrice;
}
